import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Wand2 } from 'lucide-react';
import { listOutstandingInvoices } from '@/app/services/paymentService';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileNumberInput } from '@/components/ui/MobileNumberInput';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';

export interface AllocationRow {
    invoice_voucher_id: number;
    invoice_number: string;
    transaction_date: string;
    amount_due: number;
    amount_to_allocate: string;
    selected: boolean;
}

interface OutstandingInvoicesListProps {
    partyId: number;
    paymentAmount: number;
    allocations: AllocationRow[];
    onChange: (next: AllocationRow[]) => void;
}

function toNumber(v: string): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function OutstandingInvoicesList({
    partyId,
    paymentAmount,
    allocations,
    onChange,
}: OutstandingInvoicesListProps) {
    const { data: invoices = [], isLoading } = useQuery({
        queryKey: ['outstandingInvoices', partyId],
        queryFn: () => listOutstandingInvoices(partyId),
        enabled: partyId > 0,
        staleTime: 15_000,
    });

    // Initialize allocation rows when invoices load or party changes
    useEffect(() => {
        if (invoices.length === 0) {
            if (allocations.length !== 0) onChange([]);
            return;
        }
        const existingById = new Map(allocations.map((a) => [a.invoice_voucher_id, a]));
        const sameSet =
            allocations.length === invoices.length &&
            invoices.every((inv) => existingById.has(inv.id));

        if (sameSet) return;

        const next: AllocationRow[] = invoices.map((inv) => {
            const existing = existingById.get(inv.id);
            return (
                existing ?? {
                    invoice_voucher_id: inv.id,
                    invoice_number: inv.voucher_number,
                    transaction_date: inv.transaction_date,
                    amount_due: inv.amount_due,
                    amount_to_allocate: '',
                    selected: false,
                }
            );
        });
        onChange(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoices]);

    const totalAllocated = useMemo(
        () =>
            allocations.reduce(
                (sum, a) => (a.selected ? sum + toNumber(a.amount_to_allocate) : sum),
                0
            ),
        [allocations]
    );

    const remaining = Math.max(paymentAmount - totalAllocated, 0);
    const overAllocated = totalAllocated > paymentAmount + 0.001;

    const handleAutoAllocate = () => {
        if (paymentAmount <= 0 || invoices.length === 0) return;

        const sorted = [...invoices].sort((a, b) =>
            a.transaction_date < b.transaction_date ? -1 : 1
        );

        let balance = paymentAmount;
        const byId = new Map<number, AllocationRow>();
        for (const a of allocations) byId.set(a.invoice_voucher_id, a);

        const next: AllocationRow[] = sorted.map((inv) => {
            const existing = byId.get(inv.id);
            if (balance <= 0) {
                return {
                    invoice_voucher_id: inv.id,
                    invoice_number: inv.voucher_number,
                    transaction_date: inv.transaction_date,
                    amount_due: inv.amount_due,
                    amount_to_allocate: '',
                    selected: false,
                };
            }
            const take = Math.min(inv.amount_due, balance);
            balance -= take;
            return {
                invoice_voucher_id: inv.id,
                invoice_number: inv.voucher_number,
                transaction_date: inv.transaction_date,
                amount_due: inv.amount_due,
                amount_to_allocate: take.toFixed(2),
                selected: take > 0,
                ...(existing ? {} : {}),
            };
        });

        // Preserve original order from the `allocations` array where possible
        const nextById = new Map(next.map((a) => [a.invoice_voucher_id, a]));
        const ordered = allocations.length
            ? allocations.map((a) => nextById.get(a.invoice_voucher_id) ?? a)
            : next;
        onChange(ordered);
    };

    const updateRow = (id: number, patch: Partial<AllocationRow>) => {
        onChange(
            allocations.map((a) =>
                a.invoice_voucher_id === id ? { ...a, ...patch } : a
            )
        );
    };

    const handleToggle = (row: AllocationRow) => {
        if (row.selected) {
            updateRow(row.invoice_voucher_id, {
                selected: false,
                amount_to_allocate: '',
            });
        } else {
            const fill = clamp(remaining > 0 ? remaining : row.amount_due, 0, row.amount_due);
            updateRow(row.invoice_voucher_id, {
                selected: true,
                amount_to_allocate: fill.toFixed(2),
            });
        }
    };

    const handleAmountChange = (row: AllocationRow, raw: string) => {
        updateRow(row.invoice_voucher_id, {
            selected: true,
            amount_to_allocate: raw,
        });
    };

    const handleAmountBlur = (row: AllocationRow) => {
        const value = toNumber(row.amount_to_allocate);
        if (value <= 0) {
            updateRow(row.invoice_voucher_id, {
                selected: false,
                amount_to_allocate: '',
            });
            return;
        }
        const clamped = clamp(value, 0, row.amount_due);
        updateRow(row.invoice_voucher_id, {
            selected: true,
            amount_to_allocate: clamped.toFixed(2),
        });
    };

    const handleMax = (row: AllocationRow) => {
        const takeFromRemaining = Math.max(remaining + toNumber(row.amount_to_allocate), 0);
        const fill = clamp(takeFromRemaining, 0, row.amount_due);
        updateRow(row.invoice_voucher_id, {
            selected: fill > 0,
            amount_to_allocate: fill > 0 ? fill.toFixed(2) : '',
        });
    };

    return (
        <div className="space-y-3">
            {/* Summary / Auto Allocate bar */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-card rounded-xl border border-border">
                <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Allocated</p>
                    <p
                        className={cn(
                            'text-base font-semibold',
                            overAllocated ? 'text-destructive' : 'text-foreground'
                        )}
                    >
                        ₹{totalAllocated.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        <span className="text-muted-foreground font-normal">
                            {' / '}₹
                            {paymentAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </span>
                    </p>
                </div>
                <MobileButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon={<Wand2 className="h-4 w-4" />}
                    onClick={handleAutoAllocate}
                    disabled={paymentAmount <= 0 || invoices.length === 0}
                >
                    Auto
                </MobileButton>
            </div>

            {isLoading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <MobileSkeleton key={i} className="h-24 w-full" />
                    ))}
                </div>
            ) : invoices.length === 0 ? (
                <div className="py-8">
                    <MobileEmptyState
                        icon={<FileText className="h-12 w-12" />}
                        title="No outstanding invoices"
                        description="This customer has nothing pending. The payment will be recorded as advance credit."
                    />
                </div>
            ) : (
                <div className="space-y-2">
                    {allocations.map((row) => (
                        <AllocationCard
                            key={row.invoice_voucher_id}
                            row={row}
                            onToggle={() => handleToggle(row)}
                            onAmountChange={(v) => handleAmountChange(row, v)}
                            onAmountBlur={() => handleAmountBlur(row)}
                            onMax={() => handleMax(row)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface AllocationCardProps {
    row: AllocationRow;
    onToggle: () => void;
    onAmountChange: (value: string) => void;
    onAmountBlur: () => void;
    onMax: () => void;
}

function AllocationCard({
    row,
    onToggle,
    onAmountChange,
    onAmountBlur,
    onMax,
}: AllocationCardProps) {
    const dateLabel = (() => {
        try {
            return format(new Date(row.transaction_date), 'dd MMM yyyy');
        } catch {
            return row.transaction_date;
        }
    })();

    return (
        <div
            className={cn(
                'bg-card rounded-xl border overflow-hidden transition-colors',
                row.selected ? 'border-primary' : 'border-border'
            )}
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left active:bg-accent"
            >
                <span
                    className={cn(
                        'w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0',
                        row.selected
                            ? 'bg-primary border-primary'
                            : 'bg-background border-input'
                    )}
                    aria-hidden="true"
                >
                    {row.selected && (
                        <svg
                            className="w-3 h-3 text-primary-foreground"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </span>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">
                        {row.invoice_number}
                    </p>
                    <p className="text-xs text-muted-foreground">{dateLabel}</p>
                </div>
                <p className="text-sm text-muted-foreground whitespace-nowrap">
                    Due ₹{row.amount_due.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
            </button>

            <div className="flex items-center gap-2 px-4 pb-3">
                <div className="flex-1">
                    <MobileNumberInput
                        prefix="₹"
                        placeholder="0.00"
                        value={row.amount_to_allocate}
                        onValueChange={onAmountChange}
                        onBlur={onAmountBlur}
                    />
                </div>
                <MobileButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onMax}
                    className="flex-shrink-0 px-3"
                >
                    Max
                </MobileButton>
            </div>
        </div>
    );
}
